import type {
  Objective,
  ObjectiveFieldCreate,
  ObjectiveFieldUpdate,
  MonthLock,
  Person,
  PersonFieldCreate,
  PersonFieldUpdate,
  RagScore,
  RagScoreFieldCreate,
  RagScoreFieldUpdate,
  ReportingPeriod,
  ReportingPeriodFieldCreate,
  ReportingPeriodFieldUpdate,
  TaxonomyItem,
} from "./types";

// =========================================================================
// Frontend data layer.
//
// Replaces the old src/data/graph.ts which talked directly to Microsoft
// Graph + SharePoint. The new app talks to our own API at /api/* under the
// same SWA origin. Auth is handled by SWA's session cookie - browser
// includes it automatically on same-origin requests, so these calls don't
// need an Authorization header.
//
// SQL rows come back PascalCase; we map to camelCase domain types here so
// downstream pages don't change.
// =========================================================================

interface ApiError {
  error: string;
  detail?: string;
}

async function apiFetch<T>(path: string, init?: RequestInit): Promise<T> {
  const url = path.startsWith("/") ? path : `/${path}`;
  const res = await fetch(url, init);
  if (res.status === 401) {
    // Session expired or never existed - bounce to login. The login page
    // will redirect back here on success.
    const returnTo = window.location.pathname + window.location.search;
    window.location.replace(
      `/.auth/login/aad?post_login_redirect_uri=${encodeURIComponent(returnTo)}`,
    );
    // Throw so the caller's promise chain stops cleanly while the redirect happens.
    throw new Error("Not authenticated - redirecting to sign in");
  }
  if (!res.ok) {
    let message = `${res.status} ${res.statusText}`;
    try {
      const body = (await res.json()) as ApiError;
      if (body?.error) message = body.error;
      if (body?.detail) message += ` - ${body.detail}`;
    } catch {
      // Body wasn't JSON - keep the status-line message.
    }
    throw new Error(message);
  }
  if (res.status === 204) return undefined as T;
  return (await res.json()) as T;
}

// ---- Row shapes from the API (PascalCase, match SQL columns) -------------

interface PersonRow {
  Id: number;
  FullName: string;
  Team: "Exec" | "CLT" | "User" | null;
  DepartmentId: number | null;
  DepartmentName: string | null;
  JobTitle: string | null;
  Active: boolean;
  CoveredById: number | null;
  CoveredByName: string | null;
}

interface ObjectiveRow {
  Id: number;
  PersonId: number;
  PersonName: string;
  CategoryId: number | null;
  CategoryName: string | null;
  Year: number | null;
  Status: "Draft" | "Active" | "Deleted" | null;
  ObjectiveText: string | null;
  MeasuresText: string | null;
}

interface RagScoreRow {
  Id: number;
  PersonId: number;
  PersonName: string;
  CategoryId: number;
  CategoryName: string;
  Year: number;
  Month: number;
  RagValue: 1 | 2 | 3 | null;
  Narrative: string | null;
  ReportingPeriodId: number | null;
  ReportingPeriodTitle: string | null;
}

interface MonthLockRow {
  Id: number;
  PersonId: number;
  PersonName: string;
  Year: number;
  Month: number;
  LockedAt: string;
  LockedBy: string;
}

interface ReportingPeriodRow {
  Id: number;
  Title: string;
  Year: number | null;
  StartDate: string | null;
  EndDate: string | null;
  BoardMeetingDate: string | null;
  BoardDeadlineDate: string | null;
  IsCurrent: boolean;
}

interface TaxonomyRow {
  Id: number;
  Name: string;
}

// ---- Read functions ------------------------------------------------------

export async function fetchPeople(): Promise<Person[]> {
  const rows = await apiFetch<PersonRow[]>("/api/people");
  return rows.map((r) => ({
    id: r.Id,
    fullName: r.FullName,
    team: r.Team,
    department: r.DepartmentName,
    jobTitle: r.JobTitle,
    active: r.Active,
    coveredBy: r.CoveredByName,
  }));
}

export async function fetchObjectives(): Promise<Objective[]> {
  const rows = await apiFetch<ObjectiveRow[]>("/api/objectives");
  return rows.map((r) => ({
    id: r.Id,
    personName: r.PersonName,
    category: r.CategoryName,
    year: r.Year,
    status: r.Status,
    objectiveText: r.ObjectiveText,
    measuresText: r.MeasuresText,
  }));
}

export async function fetchRagScores(): Promise<RagScore[]> {
  const rows = await apiFetch<RagScoreRow[]>("/api/rag-scores");
  return rows.map((r) => ({
    id: r.Id,
    personName: r.PersonName,
    category: r.CategoryName,
    year: r.Year,
    month: r.Month,
    ragValue: r.RagValue,
    narrative: r.Narrative,
    reportingPeriodName: r.ReportingPeriodTitle,
  }));
}

export async function fetchMonthLocks(): Promise<MonthLock[]> {
  const rows = await apiFetch<MonthLockRow[]>("/api/month-locks");
  return rows.map((r) => ({
    id: r.Id,
    personId: r.PersonId,
    personName: r.PersonName,
    year: r.Year,
    month: r.Month,
    lockedAt: r.LockedAt,
    lockedBy: r.LockedBy,
  }));
}

/**
 * Date columns may come back as ISO strings (DATE rendered by mssql) or
 * Date objects depending on the driver. Normalise to YYYY-MM-DD.
 */
function toDateOnly(value: string | Date | null): string | null {
  if (value == null) return null;
  if (value instanceof Date) {
    if (Number.isNaN(value.getTime())) return null;
    return value.toISOString().slice(0, 10);
  }
  const s = String(value);
  if (s.length >= 10) return s.slice(0, 10);
  return null;
}

export async function fetchReportingPeriods(): Promise<ReportingPeriod[]> {
  const rows = await apiFetch<ReportingPeriodRow[]>("/api/reporting-periods");
  return rows.map((r) => ({
    id: r.Id,
    title: r.Title,
    year: r.Year,
    startDate: toDateOnly(r.StartDate),
    endDate: toDateOnly(r.EndDate),
    boardMeetingDate: toDateOnly(r.BoardMeetingDate),
    boardDeadlineDate: toDateOnly(r.BoardDeadlineDate),
    isCurrent: r.IsCurrent,
  }));
}

export async function fetchCategories(): Promise<TaxonomyItem[]> {
  const rows = await apiFetch<TaxonomyRow[]>("/api/categories");
  return rows.map((r) => ({ id: r.Id, name: r.Name }));
}

export async function fetchDepartments(): Promise<TaxonomyItem[]> {
  const rows = await apiFetch<TaxonomyRow[]>("/api/departments");
  return rows.map((r) => ({ id: r.Id, name: r.Name }));
}

// ---- /api/me -------------------------------------------------------------

export interface MeResponse {
  upn: string;
  displayName: string | null;
  role: "admin" | "exec" | "clt" | "user" | null;
  groups: string[];
  isAdmin: boolean;
  isExec: boolean;
  isClt: boolean;
  isUser: boolean;
}

export async function fetchMe(): Promise<MeResponse> {
  return apiFetch<MeResponse>("/api/me");
}

// ---- /.auth/me (SWA session) --------------------------------------------

export interface SwaPrincipal {
  userId: string;
  userDetails: string;
  identityProvider?: string;
}

export interface AuthMeResponse {
  clientPrincipal: SwaPrincipal | null;
}

export async function fetchSwaSession(): Promise<SwaPrincipal | null> {
  const res = await fetch("/.auth/me");
  if (!res.ok) return null;
  const body = (await res.json()) as AuthMeResponse;
  return body.clientPrincipal ?? null;
}

// =========================================================================
// Write functions
//
// All POST/PATCH/DELETE wrappers around the SWA API. Server enforces auth
// and role - frontend can call these freely; 403 surfaces as a thrown
// Error from apiFetch.
// =========================================================================

function jsonInit(method: string, body?: unknown): RequestInit {
  const init: RequestInit = {
    method,
    headers: { "Content-Type": "application/json" },
  };
  if (body !== undefined) init.body = JSON.stringify(body);
  return init;
}

// ---- Objectives ----------------------------------------------------------

export async function createObjective(
  fields: ObjectiveFieldCreate,
): Promise<number> {
  const res = await apiFetch<{ id: number }>(
    "/api/objectives",
    jsonInit("POST", fields),
  );
  return res.id;
}

/**
 * Result of an objective PATCH that changed its scoring slot (category, year
 * or person). Undefined when the slot didn't move - the API answers 204 then.
 *
 * `scoresRetainedBySlotMate` is true when another live objective still sits
 * in the old slot, so the RAG history shared between them stayed with that
 * one rather than following this objective.
 */
export interface ObjectiveSlotMoveResult {
  scoresMoved: number;
  scoresLeftBehind: number;
  scoresRetainedBySlotMate: boolean;
}

export async function updateObjective(
  id: number,
  fields: ObjectiveFieldUpdate,
): Promise<ObjectiveSlotMoveResult | undefined> {
  return await apiFetch<ObjectiveSlotMoveResult | undefined>(
    `/api/objectives/${id}`,
    jsonInit("PATCH", fields),
  );
}

// ---- RAG scores ----------------------------------------------------------

export async function createRagScore(
  fields: RagScoreFieldCreate,
): Promise<number> {
  const res = await apiFetch<{ id: number }>(
    "/api/rag-scores",
    jsonInit("POST", fields),
  );
  return res.id;
}

export async function updateRagScore(
  id: number,
  fields: RagScoreFieldUpdate,
): Promise<void> {
  await apiFetch<void>(`/api/rag-scores/${id}`, jsonInit("PATCH", fields));
}

export async function deleteRagScore(id: number): Promise<void> {
  await apiFetch<void>(`/api/rag-scores/${id}`, { method: "DELETE" });
}

// ---- Month locks ---------------------------------------------------------

/**
 * Lock the (PersonName, Year, Month) slot. Idempotent: if a lock already
 * exists for the slot, returns the existing id rather than throwing.
 */
export async function createMonthLock(payload: {
  PersonName: string;
  Year: number;
  Month: number;
}): Promise<number> {
  const res = await apiFetch<{ id: number }>(
    "/api/month-locks",
    jsonInit("POST", payload),
  );
  return res.id;
}

/** Unlock a previously-locked slot by its lock-row id. */
export async function deleteMonthLock(id: number): Promise<void> {
  await apiFetch<void>(`/api/month-locks/${id}`, { method: "DELETE" });
}

export async function backfillReportingPeriodNames(
  // Args kept for call-site compatibility with the old SP-era helper, but
  // ignored - the server has the data. Returns the number of rows updated.
  _ragScores: RagScore[],
  _reportingPeriods: ReportingPeriod[],
): Promise<number> {
  const res = await apiFetch<{ updated: number }>(
    "/api/rag-scores/backfill-period-names",
    { method: "POST" },
  );
  return res.updated;
}

// ---- People --------------------------------------------------------------

export async function createPerson(
  fields: PersonFieldCreate,
): Promise<number> {
  const res = await apiFetch<{ id: number }>(
    "/api/people",
    jsonInit("POST", fields),
  );
  return res.id;
}

export async function updatePerson(
  id: number,
  fields: PersonFieldUpdate,
): Promise<void> {
  await apiFetch<void>(`/api/people/${id}`, jsonInit("PATCH", fields));
}

export async function reassignPersonRefs(
  fromName: string,
  toName: string,
  // Old signature included the lists for client-side cascading; ignored now,
  // server walks the rows directly.
  _objectives?: Objective[],
  _ragScores?: RagScore[],
): Promise<{ objectives: number; ragScores: number; people: number }> {
  return apiFetch<{ objectives: number; ragScores: number; people: number }>(
    "/api/people/reassign",
    jsonInit("POST", { fromName, toName }),
  );
}

// ---- Reporting periods ---------------------------------------------------

export async function createReportingPeriod(
  fields: ReportingPeriodFieldCreate,
): Promise<number> {
  const res = await apiFetch<{ id: number }>(
    "/api/reporting-periods",
    jsonInit("POST", fields),
  );
  return res.id;
}

export async function updateReportingPeriod(
  id: number,
  fields: ReportingPeriodFieldUpdate,
): Promise<void> {
  await apiFetch<void>(
    `/api/reporting-periods/${id}`,
    jsonInit("PATCH", fields),
  );
}

export async function deleteReportingPeriod(id: number): Promise<void> {
  await apiFetch<void>(`/api/reporting-periods/${id}`, { method: "DELETE" });
}

export async function setCurrentReportingPeriod(
  id: number,
  // Old signature included the periods list to know which to clear;
  // ignored now, server clears all others atomically.
  _allPeriods?: ReportingPeriod[],
): Promise<void> {
  await apiFetch<void>(
    `/api/reporting-periods/${id}/set-current`,
    { method: "POST" },
  );
}

// ---- Categories ----------------------------------------------------------

export async function createCategory(name: string): Promise<number> {
  const res = await apiFetch<{ id: number }>(
    "/api/categories",
    jsonInit("POST", { name }),
  );
  return res.id;
}

export async function deleteCategory(id: number): Promise<void> {
  await apiFetch<void>(`/api/categories/${id}`, { method: "DELETE" });
}

/**
 * Server-side cascading rename: updates Categories.Name + Objectives.Category
 * + RagScores.Category in one transaction. Old client-side cascade signature
 * (oldName, newName, objectives, ragScores) reduced to (oldName, newName) -
 * the extra args are accepted but ignored for backwards-compat with the
 * existing CategoriesPage.
 */
export async function renameCategory(
  oldName: string,
  newName: string,
  _objectives?: Objective[],
  _ragScores?: RagScore[],
): Promise<{ objectives: number; ragScores: number }> {
  return apiFetch<{ objectives: number; ragScores: number }>(
    "/api/categories/rename",
    jsonInit("PATCH", { oldName, newName }),
  );
}

// ---- Departments ---------------------------------------------------------

export async function createDepartment(name: string): Promise<number> {
  const res = await apiFetch<{ id: number }>(
    "/api/departments",
    jsonInit("POST", { name }),
  );
  return res.id;
}

export async function deleteDepartment(id: number): Promise<void> {
  await apiFetch<void>(`/api/departments/${id}`, { method: "DELETE" });
}
